import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as AttrUtils from 'Util/AttributeUtils';

export interface IGiftCertificateEntityAttributes extends IModelAttributes {
	description: string;
	code: string;
	initialValue: number;
	expiryDate: Date;
	legacyId: number;
	disabled: boolean;
	price: number;
	recipientEmail?: string;
	message?: string;

	giftCertificateUsages: Array<Models.GiftCertificateUsageEntity | Models.IGiftCertificateUsageEntityAttributes>;
	userId?: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;
	transaction?: Models.TransactionEntity | Models.ITransactionEntityAttributes;
}
@entity('GiftCertificateEntity', 'Gift Certificate')
export default class GiftCertificateEntity extends Model implements IGiftCertificateEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<GiftCertificateEntity, string>()
	public description: string;

	@observable
	@attribute<GiftCertificateEntity, string>()
	public code: string;

	@observable
	@attribute<GiftCertificateEntity, number>()
	public initialValue: number;

	@observable
	@attribute<GiftCertificateEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public expiryDate: Date;

	@observable
	@attribute<GiftCertificateEntity, number>()
	public legacyId: number;

	/**
	 * Whether or not the gift certificate has been disabled by staff
	 */
	@observable
	@attribute<GiftCertificateEntity, boolean>()
	public disabled: boolean;

	/**
	 * The amount paid for this gift certificate.
	 */
	@observable
	@attribute<GiftCertificateEntity, number>()
	public price: number;

	/**
	 * The email address of the intended recipient (for gifted certificates).
	 */
	@observable
	@attribute<GiftCertificateEntity, string>()
	public recipientEmail?: string;

	/**
	 * Optional personal message to include in the gift certificate email.
	 */
	@observable
	@attribute<GiftCertificateEntity, string>()
	public message?: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public giftCertificateUsages: Models.GiftCertificateUsageEntity[] = [];

	@observable
	@attribute()
	public userId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public transaction?: Models.TransactionEntity;

	constructor(attributes?: Partial<IGiftCertificateEntityAttributes>) {
		super(attributes);

		this.giftCertificateUsages = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IGiftCertificateEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.code !== undefined) {
				this.code = attributes.code;
			}
			if (attributes.initialValue !== undefined) {
				this.initialValue = attributes.initialValue;
			}
			if (attributes.expiryDate !== undefined) {
				if (attributes.expiryDate === null) {
					this.expiryDate = attributes.expiryDate;
				} else {
					this.expiryDate = moment(attributes.expiryDate).toDate();
				}
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.price !== undefined) {
				this.price = attributes.price;
			}
			if (attributes.recipientEmail !== undefined) {
				this.recipientEmail = attributes.recipientEmail;
			}
			if (attributes.message !== undefined) {
				this.message = attributes.message;
			}
			if (attributes.giftCertificateUsages !== undefined && Array.isArray(attributes.giftCertificateUsages)) {
				if (!this.giftCertificateUsages) {
					this.giftCertificateUsages = [];
				}
				for (const model of attributes.giftCertificateUsages) {
					if (model instanceof Models.GiftCertificateUsageEntity) {
						this.giftCertificateUsages.push(model);
					} else {
						this.giftCertificateUsages.push(new Models.GiftCertificateUsageEntity(model));
					}
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (attributes.transaction !== undefined) {
				if (attributes.transaction === null) {
					this.transaction = attributes.transaction;
				} else if (attributes.transaction instanceof Models.TransactionEntity) {
					this.transaction = attributes.transaction;
				} else {
					this.transaction = new Models.TransactionEntity(attributes.transaction);
				}
			}
		}
	}
}
